"use client";

import type { BaseComponentProps } from "@json-render/react";
import { useStateValue } from "@json-render/react";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  shouldAllowRankingSortControl,
  sortAndLimitRankingRows,
} from "@/lib/ai/visualization/views/shared/ranking-rows";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "./ai-chart";
import {
  resolveBarRankingSizing,
  resolveBarSeparatorStyle,
} from "./bar-presentation";
import { renderSquareZeroSafeBarShape } from "./bar-shape";
import {
  analyzeCategoricalAxisLabels,
  formatCategoryTickLabel,
  resolveCategoryAxisWidth,
  resolveRequestedChartHeight,
} from "./categorical-chart-core";
import {
  coerceNumber,
  DEFAULT_EXPORT_FORMATS,
  EMPTY_VIZ_ROWS,
  formatPercentTick,
  formatValueTick,
  formatVizTooltipMetricValue,
  isRecord,
  normalizeVizExportFormats,
  resolveEffectiveXAxisTickDensity,
  resolveVizAxisLineProps,
  resolveVizAxisStyle,
  resolveVizAxisTickLineProps,
  resolveVizCartesianGridProps,
  resolveVizPaletteMode,
  resolveVizReferenceLabelProps,
  resolveVizReferenceLines,
  resolveVizSemanticReferenceLineProps,
  resolveVizSeriesColor,
  resolveVizTooltipMode,
  resolveXAxisTickDensityConfig,
  resolveXAxisTickLayout,
  type VizAxisPreset,
  type VizPaletteMode,
  type VizReferenceLine,
  type VizTooltipMode,
  type VizXAxisTickDensity,
} from "./chart-format";
import {
  buildVizDatasetFieldsPath,
  buildVizFieldMetaMap,
  isPercentFieldKey,
  parseVizDatasetFields,
} from "./dataset-fields";
import {
  formatVizAxisDateLabel,
  formatVizTooltipDomainLabel,
} from "./date-format";
import {
  copyVizAsPng,
  downloadRowsAsCsv,
  downloadRowsAsJson,
  downloadVizAsPng,
} from "./export-utils";
import { resolveCategoryLabel } from "./label-resolver";
import {
  VizActionBar,
  VizExportMenu,
  type VizSortDirection,
  VizSortMenu,
} from "./viz-actions";

export type BarRankingProps = BaseComponentProps<{
  dataPath: string;
  categoryKey: string;
  valueKey: string;
  limit?: number | null;
  layout?: "vertical" | "horizontal" | null;
  chartHeight?: number | null;
  sort?: "desc" | "asc" | "none" | null;
  categoryLabel?: string | null;
  valueLabel?: string | null;
  axisPreset?: VizAxisPreset | null;
  tooltipMode?: VizTooltipMode | null;
  paletteMode?: VizPaletteMode | null;
  xAxisTicks?: { density?: VizXAxisTickDensity | null } | null;
  referenceLines?: VizReferenceLine[] | null;
  allowSortControl?: boolean | null;
  allowExport?: boolean | null;
  exportFormats?: string[] | null;
}>;

const MIN_HORIZONTAL_HEIGHT = 280;
const DEFAULT_HORIZONTAL_HEIGHT = 320;
const MIN_VERTICAL_HEIGHT = 280;
const MAX_VERTICAL_HEIGHT = 520;
const MAX_HORIZONTAL_HEIGHT = 420;
const ROW_SLOT_HEIGHT = 30;
const VERTICAL_PADDING = 48;
const MIN_HORIZONTAL_PLOT_WIDTH = 720;
const MIN_VERTICAL_PLOT_WIDTH = 720;

function resolveCategoryTickMaxLength(args: {
  axisPreset?: VizAxisPreset | null;
  isMobile: boolean;
  isVertical: boolean;
}): number {
  if (!args.isVertical) return 18;
  if (args.axisPreset === "readable") {
    return args.isMobile ? 24 : 28;
  }
  if (args.axisPreset === "compact") {
    return args.isMobile ? 16 : 18;
  }
  return args.isMobile ? 20 : 24;
}

function resolveCategoryAxisMaxWidth(args: {
  axisPreset?: VizAxisPreset | null;
  isMobile: boolean;
  isVertical: boolean;
}): number {
  if (!args.isVertical) return 220;
  if (args.axisPreset === "readable") {
    return args.isMobile ? 220 : 300;
  }
  if (args.axisPreset === "compact") {
    return args.isMobile ? 160 : 220;
  }
  return args.isMobile ? 200 : 260;
}

/**
 * 単一指標のランキング棒グラフを描画する。
 *
 * @remarks
 * 既存 UI と独立して AI チャートの読みやすさを改善するため、
 * axis/palette/tooltip を props から制御し、密度の高いカテゴリでも可読性を維持する。
 */
export function BarRanking({ props }: BarRankingProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const {
    dataPath,
    categoryKey,
    valueKey,
    limit,
    layout,
    chartHeight: requestedChartHeight,
    sort,
    categoryLabel,
    valueLabel,
    axisPreset,
    tooltipMode,
    paletteMode,
    xAxisTicks,
    referenceLines,
    allowSortControl,
    allowExport,
    exportFormats,
  } = props;
  const resolvedTooltipMode = resolveVizTooltipMode(tooltipMode);
  const resolvedPaletteMode = resolveVizPaletteMode(paletteMode);
  const rawData = useStateValue<unknown>(dataPath);
  const fieldsPath = useMemo(
    () => buildVizDatasetFieldsPath(dataPath),
    [dataPath],
  );
  const rawFields = useStateValue<unknown>(fieldsPath);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return EMPTY_VIZ_ROWS;
    return rawData.filter(isRecord);
  }, [rawData]);
  const chartLayout = layout ?? "vertical";
  const isVertical = chartLayout === "vertical";

  const [interactiveSort, setInteractiveSort] = useState<VizSortDirection>(
    sort ?? "desc",
  );

  const parsedFields = useMemo(
    () => parseVizDatasetFields(rawFields),
    [rawFields],
  );
  const metaByName = useMemo(
    () => buildVizFieldMetaMap(parsedFields),
    [parsedFields],
  );

  const visibleData = useMemo(
    () =>
      sortAndLimitRankingRows({
        rows: data,
        sort: interactiveSort,
        limit,
        getValue: (row) => coerceNumber(row[valueKey]),
      }),
    [data, interactiveSort, limit, valueKey],
  );

  const categoryFieldMeta = metaByName.get(categoryKey);
  const categoryTickMaxLength = useMemo(
    () =>
      resolveCategoryTickMaxLength({
        axisPreset,
        isMobile,
        isVertical,
      }),
    [axisPreset, isMobile, isVertical],
  );
  const categoryAxisMaxWidth = useMemo(
    () =>
      resolveCategoryAxisMaxWidth({
        axisPreset,
        isMobile,
        isVertical,
      }),
    [axisPreset, isMobile, isVertical],
  );
  const formatCategoryAxisTick = useCallback(
    (value: unknown) => {
      const resolved = resolveCategoryLabel({
        value,
        fieldMeta: categoryFieldMeta,
        i18n,
      });
      return formatCategoryTickLabel({
        label: formatVizAxisDateLabel(resolved, i18n),
        maxLength: categoryTickMaxLength,
      });
    },
    [categoryFieldMeta, categoryTickMaxLength, i18n],
  );

  const formatCategoryTooltipLabel = useCallback(
    (value: unknown) => {
      const resolved = resolveCategoryLabel({
        value,
        fieldMeta: categoryFieldMeta,
        i18n,
      });
      const formatted = formatVizTooltipDomainLabel(resolved, i18n);
      return categoryLabel ? `${categoryLabel}: ${formatted}` : formatted;
    },
    [categoryFieldMeta, categoryLabel, i18n],
  );

  const rowCount = visibleData.length;
  const resolvedReferenceLines = useMemo(
    () => resolveVizReferenceLines(referenceLines),
    [referenceLines],
  );

  const verticalChartHeight = rowCount * ROW_SLOT_HEIGHT + VERTICAL_PADDING;
  const chartHeight = useMemo(() => {
    const requested = resolveRequestedChartHeight(requestedChartHeight);
    if (requested !== null) {
      if (isVertical) {
        return Math.max(
          MIN_VERTICAL_HEIGHT,
          Math.min(MAX_VERTICAL_HEIGHT, requested),
        );
      }
      return Math.max(
        MIN_HORIZONTAL_HEIGHT,
        Math.min(MAX_HORIZONTAL_HEIGHT, requested),
      );
    }
    if (!isVertical) return DEFAULT_HORIZONTAL_HEIGHT;
    return Math.max(
      MIN_VERTICAL_HEIGHT,
      Math.min(MAX_VERTICAL_HEIGHT, verticalChartHeight),
    );
  }, [isVertical, requestedChartHeight, verticalChartHeight]);
  const plotMinWidth = useMemo(() => {
    if (isVertical) return MIN_VERTICAL_PLOT_WIDTH;
    const perCategoryWidth = 44;
    return Math.max(MIN_HORIZONTAL_PLOT_WIDTH, rowCount * perCategoryWidth);
  }, [isVertical, rowCount]);

  const yAxisInterval = useMemo(() => {
    if (!isVertical) return 0;
    const maxVisibleLabels = Math.max(
      1,
      Math.floor((chartHeight - VERTICAL_PADDING) / ROW_SLOT_HEIGHT),
    );
    if (rowCount <= maxVisibleLabels) return 0;
    return Math.ceil(rowCount / maxVisibleLabels) - 1;
  }, [chartHeight, isVertical, rowCount]);

  const yAxisWidth = useMemo(() => {
    return resolveCategoryAxisWidth({
      rows: visibleData,
      categoryKey,
      formatCategoryAxisTick,
      maxLabelLength: categoryTickMaxLength,
      maxWidth: categoryAxisMaxWidth,
    });
  }, [
    categoryAxisMaxWidth,
    categoryKey,
    categoryTickMaxLength,
    formatCategoryAxisTick,
    visibleData,
  ]);

  const chartConfig = useMemo(() => {
    const label =
      valueLabel?.trim() || metaByName.get(valueKey)?.label || valueKey;
    const color = resolveVizSeriesColor({
      index: 0,
      paletteMode: resolvedPaletteMode,
    });
    return {
      [valueKey]: {
        label,
        color,
      },
    };
  }, [metaByName, resolvedPaletteMode, valueKey, valueLabel]);

  const barColor = useMemo(() => {
    return chartConfig[valueKey]?.color ?? "var(--chart-1)";
  }, [chartConfig, valueKey]);

  const isPercentMetric = useMemo(() => {
    return isPercentFieldKey(valueKey, metaByName);
  }, [metaByName, valueKey]);
  const formatTooltipValue = useCallback(
    (value: unknown) =>
      formatVizTooltipMetricValue(value, {
        isPercent: isPercentMetric,
      }),
    [isPercentMetric],
  );

  const categoryAxisLabelAnalysis = useMemo(
    () =>
      analyzeCategoricalAxisLabels({
        rows: visibleData,
        categoryKey,
        formatCategoryAxisTick,
        isVertical,
      }),
    [categoryKey, formatCategoryAxisTick, isVertical, visibleData],
  );
  const rotateXAxis = categoryAxisLabelAnalysis.shouldRotate;
  const xAxisTickLayout = useMemo(() => {
    return resolveXAxisTickLayout({
      rotate: rotateXAxis,
      labels: categoryAxisLabelAnalysis.sampleLabels,
    });
  }, [categoryAxisLabelAnalysis.sampleLabels, rotateXAxis]);
  const xAxisTickDensityConfig = useMemo(() => {
    return resolveXAxisTickDensityConfig({
      density: resolveEffectiveXAxisTickDensity({
        density: xAxisTicks?.density,
        isMobile,
      }),
      rowCount,
      baseMinTickGap: xAxisTickLayout.minTickGap,
    });
  }, [isMobile, rowCount, xAxisTickLayout.minTickGap, xAxisTicks?.density]);
  const axisStyle = useMemo(
    () =>
      resolveVizAxisStyle({
        preset: axisPreset,
        isMobile,
      }),
    [axisPreset, isMobile],
  );
  const barSizing = useMemo(() => {
    return resolveBarRankingSizing({
      chartHeight,
      rowCount,
      isVertical,
      verticalPadding: VERTICAL_PADDING,
    });
  }, [chartHeight, isVertical, rowCount]);
  const barSeparatorStyle = useMemo(
    () => resolveBarSeparatorStyle({ stacking: "grouped" }),
    [],
  );

  const resolvedAllowSortControl = useMemo(
    () =>
      shouldAllowRankingSortControl({
        requested: allowSortControl,
        limit,
      }),
    [allowSortControl, limit],
  );
  const resolvedAllowExport = allowExport !== false;
  const resolvedExportFormats = normalizeVizExportFormats(
    exportFormats,
    DEFAULT_EXPORT_FORMATS,
  );
  const handleCopyImage = useCallback(async () => {
    await copyVizAsPng({
      element: containerRef.current,
    });
  }, []);

  return (
    <div ref={containerRef} className="w-full">
      <VizActionBar>
        {resolvedAllowSortControl ? (
          <VizSortMenu value={interactiveSort} onChange={setInteractiveSort} />
        ) : null}
        {resolvedAllowExport ? (
          <VizExportMenu
            formats={resolvedExportFormats}
            onCopyImage={handleCopyImage}
            onExport={async (format) => {
              if (format === "csv") {
                downloadRowsAsCsv({
                  rows: visibleData,
                  columns: [categoryKey, valueKey],
                  baseName: "viz-bar-ranking",
                });
                return;
              }

              if (format === "json") {
                downloadRowsAsJson({
                  rows: visibleData,
                  columns: [categoryKey, valueKey],
                  baseName: "viz-bar-ranking",
                });
                return;
              }

              if (format === "png") {
                await downloadVizAsPng({
                  element: containerRef.current,
                  baseName: "viz-bar-ranking",
                });
              }
            }}
          />
        ) : null}
      </VizActionBar>

      <div data-slot="bar-ranking-chart-shell" className="w-full">
        <div
          data-slot="bar-ranking-chart-scrollport"
          data-viz-capture-horizontal-scrollport="true"
          className="w-full min-w-0 overflow-x-auto pb-1"
        >
          <div
            data-slot="bar-ranking-plot"
            data-viz-capture-horizontal-content="true"
            className="overflow-hidden"
            style={plotMinWidth ? { minWidth: `${plotMinWidth}px` } : undefined}
          >
            <ChartContainer
              config={chartConfig}
              className="w-full aspect-auto rounded-[18px]"
              style={{ height: `${chartHeight}px` }}
            >
              <BarChart
                data={visibleData}
                layout={chartLayout}
                margin={{
                  top: 18,
                  right: 20,
                  bottom: isVertical ? 10 : xAxisTickLayout.bottomMargin + 8,
                  left: 4,
                }}
                barCategoryGap={barSizing.barCategoryGap}
                barGap={barSizing.barGap}
              >
                <CartesianGrid
                  {...resolveVizCartesianGridProps({ axisStyle })}
                />
                {isVertical ? (
                  <>
                    <XAxis
                      type="number"
                      dataKey={valueKey}
                      tickFormatter={
                        isPercentMetric ? formatPercentTick : formatValueTick
                      }
                      tickLine={resolveVizAxisTickLineProps()}
                      axisLine={resolveVizAxisLineProps()}
                      tickMargin={axisStyle.tickMargin}
                      tick={{ fontSize: axisStyle.tickFontSize }}
                    />
                    <YAxis
                      type="category"
                      dataKey={categoryKey}
                      width={yAxisWidth}
                      interval={yAxisInterval}
                      tickLine={resolveVizAxisTickLineProps()}
                      axisLine={resolveVizAxisLineProps()}
                      tickFormatter={formatCategoryAxisTick}
                      tickMargin={axisStyle.tickMargin}
                      tick={{ fontSize: axisStyle.tickFontSize }}
                    />
                  </>
                ) : (
                  <>
                    <XAxis
                      type="category"
                      dataKey={categoryKey}
                      tickLine={resolveVizAxisTickLineProps()}
                      axisLine={resolveVizAxisLineProps()}
                      tickFormatter={formatCategoryAxisTick}
                      tickMargin={Math.max(
                        xAxisTickLayout.tickMargin,
                        axisStyle.tickMargin,
                      )}
                      minTickGap={xAxisTickDensityConfig.minTickGap}
                      interval={xAxisTickDensityConfig.interval}
                      angle={xAxisTickLayout.angle}
                      textAnchor={xAxisTickLayout.textAnchor}
                      height={xAxisTickLayout.height}
                      tick={
                        rotateXAxis
                          ? {
                              dx: xAxisTickLayout.tickDx,
                              dy: xAxisTickLayout.tickDy,
                              fontSize: axisStyle.tickFontSize,
                            }
                          : { fontSize: axisStyle.tickFontSize }
                      }
                    />
                    <YAxis
                      type="number"
                      dataKey={valueKey}
                      tickFormatter={
                        isPercentMetric ? formatPercentTick : formatValueTick
                      }
                      tickLine={resolveVizAxisTickLineProps()}
                      axisLine={resolveVizAxisLineProps()}
                      tickMargin={axisStyle.tickMargin}
                      width={axisStyle.yAxisWidth}
                      tick={{ fontSize: axisStyle.tickFontSize }}
                    />
                  </>
                )}
                {resolvedReferenceLines.map((line) => (
                  <ReferenceLine
                    key={line.key}
                    {...(isVertical ? { x: line.value } : { y: line.value })}
                    {...resolveVizSemanticReferenceLineProps({
                      color: line.color,
                    })}
                    ifOverflow="extendDomain"
                    label={resolveVizReferenceLabelProps({
                      label: line.label,
                      color: line.color,
                    })}
                  />
                ))}
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelKey={categoryKey}
                      labelClassName={
                        resolvedTooltipMode === "compact"
                          ? "text-[11px]"
                          : undefined
                      }
                      nameKey="dataKey"
                      labelFormatter={formatCategoryTooltipLabel}
                      formatToPercentFields={isPercentMetric ? [valueKey] : []}
                      valueFormatter={formatTooltipValue}
                    />
                  }
                />
                <Bar
                  dataKey={valueKey}
                  fill={barColor}
                  isAnimationActive={false}
                  animationBegin={0}
                  animationDuration={0}
                  barSize={barSizing.barSize}
                  shape={renderSquareZeroSafeBarShape}
                  stroke="hsl(var(--background))"
                  strokeOpacity={barSeparatorStyle.strokeOpacity}
                  strokeWidth={barSeparatorStyle.strokeWidth}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
